/**
 * Provides approved, deeply immutable marketing content. Exports take no
 * runtime inputs, return shared frozen content values, and have no side effects.
 *
 * @module marketing/content
 */

function deepFreeze(value) {
  if (value && typeof value === 'object' && !Object.isFrozen(value)) {
    Object.values(value).forEach(deepFreeze);
    Object.freeze(value);
  }

  return value;
}

/**
 * Approved prompts and response options for the reflection preview. This
 * collection takes no inputs, is exported as content data, and has no side
 * effects.
 */
export const REFLECTION_QUESTIONS = deepFreeze([
  {
    id: 'planning-rhythm',
    prompt: 'Do you prefer knowing the plan or figuring it out as you go?',
    options: [
      'I like knowing the plan',
      'I like figuring it out as I go',
      'It depends on the day',
    ],
  },
  {
    id: 'rooted-or-spontaneous',
    prompt: 'In a relationship, which do you value more?',
    options: [
      'Deep rootedness & routine',
      'A balance of both',
      'Spontaneity & adventure',
    ],
  },
  {
    id: 'reply-rhythm',
    prompt: 'A match replies warmly but slowly. What is your first instinct?',
    options: [
      'I assume they were busy',
      'I feel anxious',
      'I slow down too',
      'I ask about their rhythm',
    ],
  },
  {
    id: 'repair-rhythm',
    prompt: 'When something hurts, what feels more natural?',
    options: ['Talk now', 'Meet in the middle', 'Give me time to process'],
  },
  {
    id: 'closeness-balance',
    prompt: 'What balance feels right for closeness?',
    options: ['Lots of togetherness', 'Room for both', 'Strong independence'],
  },
]);

/**
 * Approved category labels for the taste preview. This collection takes no
 * inputs, is exported as content data, and has no side effects.
 */
export const TASTE_DOMAINS = deepFreeze([
  'Music',
  'Food & Dining',
  'Travel',
  'Books',
  'Movies & Shows',
]);

/**
 * Approved engagement levels for taste selections. This collection takes no
 * inputs, is exported as content data, and has no side effects.
 */
export const TASTE_INTENSITIES = deepFreeze(['Casual', 'Regular', 'Core']);

/**
 * Duration in milliseconds for an experience option transition. This value
 * takes no inputs, is exported as timing configuration, and has no side effects.
 */
export const EXPERIENCE_TRANSITION_MS = 360;

/**
 * Approved life chapters and their adaptive prompts. This collection takes no
 * inputs, is exported as content data, and has no side effects.
 */
export const LIFE_CHAPTERS = deepFreeze([
  {
    id: 'student',
    label: 'Student',
    prompt: 'Where do you study?',
    options: ['Undergrad life', 'Grad school & research', 'Self-directed study'],
  },
  {
    id: 'early-career',
    label: 'Early Career',
    prompt: 'What are you growing into?',
    options: ['Finding my footing', 'Stepping into leadership', 'Pivoting my path'],
  },
  {
    id: 'established',
    label: 'Established',
    prompt: 'What have you made room for now?',
    options: ['Deepening roots', 'Creative pursuits', 'Work-life harmony'],
  },
  {
    id: 'building',
    label: 'Building something',
    prompt: 'What venture are you building?',
    options: ['Early-stage startup', 'Creative studio or freelance', 'A passion project'],
  },
  {
    id: 'transitioning',
    label: 'Transitioning',
    prompt: 'What are you moving toward?',
    options: ['A new city or environment', 'A fresh career direction', 'A new lifestyle design'],
  },
  {
    id: 'nomadic',
    label: 'Nomadic',
    prompt: 'Where feels like your anchor?',
    options: ['A backpack & passport', 'Split between cities', 'Temporarily settled'],
  },
]);

/**
 * Ordered identifiers for the guided landing-page chapters. This collection
 * takes no inputs, is exported as content data, and has no side effects.
 */
export const CHAPTER_IDS = deepFreeze([
  'hero',
  'principles',
  'window',
  'reflection',
  'focus',
  'taste',
  'life-chapter',
  'language',
  'resonance',
  'finale',
]);
